import { mutateData } from "@/core/api/api-methods";
import {
  getHttpStatus,
  isIdempotencyInProgress,
} from "@/core/api/api-error-handler";
import {
  clearPersistedIdempotencyKey,
  fingerprintBody,
  getOrCreatePersistedIdempotencyKey,
} from "@/core/api/idempotency";

const sleep = (ms: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });

/**
 * Mutation authentifiée avec Idempotency-Key persistée (guide §4.7).
 *
 * - même corps + même onglet (reload / retry après coupure) → même clé ;
 * - 409 (traitement en cours) → attendre puis rejouer la MÊME clé ;
 * - 422 (règle métier ou corps différent) → oublier la clé, prochaine
 *   tentative = nouvelle clé.
 */
export const mutateIdempotent = async <T>({
  scopeId,
  url,
  body,
  method = "POST",
  max409Attempts = 4,
}: {
  /** Identifiant d'endpoint, ex. `wallet.transactions.initiate`. */
  scopeId: string;
  url: string;
  body: unknown;
  method?: "POST" | "PUT" | "PATCH";
  max409Attempts?: number;
}): Promise<T> => {
  const fingerprint = fingerprintBody(body);
  const key = getOrCreatePersistedIdempotencyKey(scopeId, fingerprint);
  let lastError: unknown;

  for (let attempt = 0; attempt < max409Attempts; attempt += 1) {
    try {
      return (await mutateData({
        url,
        reqBody: body as Record<string, unknown>,
        method,
        idempotencyKey: key,
      })) as T;
    } catch (error) {
      if (getHttpStatus(error) === 422) {
        clearPersistedIdempotencyKey(scopeId, fingerprint);
        throw error;
      }
      if (!isIdempotencyInProgress(error) || attempt === max409Attempts - 1) {
        throw error;
      }
      lastError = error;
      await sleep(500 * 2 ** attempt);
    }
  }

  throw lastError;
};
